import React, { useState } from 'react';
import { Thermometer, Zap, Box, AlertTriangle } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import { getLensLegendConfig, calculateLensSummary } from '../../utils/lenses.logic';

export const LensLegend: React.FC = () => {
  const { activeLens, racks, telemetryData, whatIfActive, whatIfData } = useEditorStore(
    useShallow(state => ({
      activeLens: state.activeLens,
      racks: state.racks,
      telemetryData: state.telemetryData,
      whatIfActive: state.whatIfActive,
      whatIfData: state.whatIfData,
    }))
  );

  const [isMinimized, setIsMinimized] = useState(false);

  if (activeLens === 'none') return null;

  const currentTelemetry = whatIfActive ? whatIfData : telemetryData;
  const config = getLensLegendConfig(activeLens);
  const summary = calculateLensSummary(activeLens, Object.values(racks), currentTelemetry);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        zIndex: 30,
        background: 'rgba(13, 17, 23, 0.94)',
        backdropFilter: 'blur(12px)',
        border: '1px solid #1e293b',
        borderRadius: 8,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.55)',
        color: '#f1f5f9',
        fontSize: 11,
        fontFamily: 'Inter, sans-serif',
        minWidth: 260,
        maxWidth: 320,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        userSelect: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderBottom: isMinimized ? 'none' : '1px solid #1e293b',
          cursor: 'pointer',
        }}
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {activeLens === 'temperature' && <Thermometer size={14} color="#38bdf8" />}
          {activeLens === 'power' && <Zap size={14} color="#f59e0b" />}
          {activeLens === 'space' && <Box size={14} color="#a855f7" />}
          {activeLens === 'spof' && <AlertTriangle size={14} color="#ef4444" />}
          <span style={{ fontWeight: 600, color: '#60a5fa' }}>{config.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {config.unit}
          </span>
          <button
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: 12,
              padding: '0 2px',
            }}
            title={isMinimized ? 'Развернуть легенду' : 'Свернуть легенду'}
          >
            {isMinimized ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div style={{ padding: '10px 12px' }}>
          {/* Subtitle / standard */}
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 8 }}>
            {config.standard}
          </div>

          {/* Color steps scale */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 }}>
            {config.steps.map((step, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: step.color,
                      boxShadow: `0 0 6px ${step.color}44`,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: '#cbd5e1' }}>{step.label}</span>
                </div>
                <span
                  style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    color: '#94a3b8',
                    fontSize: 10,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {step.range}
                </span>
              </div>
            ))}
          </div>

          {/* Summary stats */}
          <div
            style={{
              borderTop: '1px solid #1e293b',
              paddingTop: 8,
              marginTop: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 10,
              color: '#94a3b8',
            }}
          >
            {activeLens === 'temperature' && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Ср: <strong style={{ color: '#f1f5f9' }}>{summary.avgValue}</strong></span>
                <span>Пик: <strong style={{ color: '#f97316' }}>{summary.maxValue}</strong></span>
                <span>
                  &gt;30°C:{' '}
                  <strong style={{ color: (summary.alertCount || 0) > 0 ? '#ef4444' : '#22c55e' }}>
                    {summary.alertCount} шт.
                  </strong>
                </span>
              </div>
            )}

            {activeLens === 'power' && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Нагрузка: <strong style={{ color: '#f1f5f9' }}>{summary.totalLoadKw} кВт</strong></span>
                <span>Лимит: <strong style={{ color: '#94a3b8' }}>{summary.totalCapacityKw} кВт</strong></span>
                <span>
                  Итог:{' '}
                  <strong style={{ color: (summary.avgPercent || 0) > 90 ? '#ef4444' : '#22c55e' }}>
                    {summary.avgPercent}%
                  </strong>
                </span>
              </div>
            )}

            {activeLens === 'space' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Занято: <strong style={{ color: '#f1f5f9' }}>{summary.totalUsedU} U</strong></span>
                  <span>Свободно: <strong style={{ color: '#38bdf8' }}>{summary.freeU ?? 0} U</strong></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Ёмкость: <strong style={{ color: '#94a3b8' }}>{summary.totalCapacityU} U</strong></span>
                  <span>
                    Доля:{' '}
                    <strong style={{ color: (summary.avgPercent || 0) > 90 ? '#ef4444' : '#22c55e' }}>
                      {summary.avgPercent}%
                    </strong>
                  </span>
                </div>
              </div>
            )}

            {activeLens === 'spof' && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Защищено 2N: <strong style={{ color: '#22c55e' }}>{summary.protectedCount}</strong></span>
                <span>
                  Риск SPOF:{' '}
                  <strong style={{ color: (summary.spofCount || 0) > 0 ? '#ef4444' : '#22c55e' }}>
                    {summary.spofCount}
                  </strong>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
